import React, { useMemo, useState, useEffect } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import {
  getIOTDocument,
  onUpdateIOTDocument,
} from '../../graphql/iot-documents';
import {
  formatValue,
  formatEnergyAutoUnit,
  formatPowerAutoUnit,
  createReconnectingSubscription,
} from '../../utils';
import { getRendimentoHidraulico } from '../../services';
import * as s from './styled-machine-analytics';

// MachineAnalytics — visao "usina" da pagina. Substitui a logica que era
// "selecionar 1 maquina e ver detalhes" por uma visao comparativa de TODAS
// as maquinas lado-a-lado:
//
//   1) Heatstrip: barra horizontal com 1 celula por maquina, altura
//      proporcional a energia gerada/consumida. Operador identifica em 1
//      segundo qual maquina esta puxando o desempenho pra baixo.
//   2) Lista compacta com nome + potencia + vazao + rendimento + status
//      por maquina, ordenavel.
//   3) Rendimento medio da usina + per-maquina coloriado.
//
// Faz subscription propria — nao depende de informacoes-aplicacao porque
// ambos podem ser usados separadamente. Reconexao automatica via
// createReconnectingSubscription.
//
// Props:
//   - things: array de { thing, id, type, model } da aplicacao
//   - type: 'ucha' | 'eba' (define energia gerada vs consumida)
//   - onMachineClick(thing): callback ao clicar numa maquina (sincroniza
//     o carrossel de ConfigAplicacao, abre detalhes, etc.)
//   - selectedMachineId: id da maquina atualmente selecionada (highlight)
const MachineAnalytics = ({
  things,
  type,
  onMachineClick,
  selectedMachineId,
}) => {
  const [thingsData, setThingsData] = useState([]);
  const [rendimentos, setRendimentos] = useState({});
  const [sortBy, setSortBy] = useState('energy'); // energy | rendimento | potencia | vazao

  // Mesma estrategia da informacoes-aplicacao: acumulador unico, mounted
  // gating, recons[] pra cleanup robusto.
  useEffect(() => {
    let mounted = true;
    const recons = [];
    const acc = [];

    Promise.all([getIOTDocument(), onUpdateIOTDocument()]).then(
      ([iotQuery, updateIotSubscription]) => {
        if (!mounted) return;
        things.forEach(async ({ thing }, index) => {
          try {
            const response = await API.graphql(
              graphqlOperation(iotQuery, { thingName: thing })
            );
            const iot =
              'getIOT' in response ? response.getIOT : response.data?.getIOT;
            if (!iot || !mounted) return;
            acc[index] = iot;
            setThingsData([...acc]);

            const recon = createReconnectingSubscription({
              label: `machine-analytics/${thing}`,
              buildSubscription: ({ next, error }) =>
                API.graphql(
                  graphqlOperation(updateIotSubscription, { thingName: thing })
                ).subscribe({ next, error }),
              onNext: ({ value }) => {
                const update = value && value.data && value.data.onUpdateIOT;
                if (!update || !mounted) return;
                acc[index] = update;
                setThingsData([...acc]);
              },
            });
            recons.push(recon);
          } catch (_) {
            // sem notifyError aqui — informacoes-aplicacao ja notifica nessa rota
          }
        });
      }
    );

    return () => {
      mounted = false;
      recons.forEach((r) => {
        if (r && typeof r.unsubscribe === 'function') r.unsubscribe();
      });
    };
  }, [things]);

  // Rendimento hidraulico por thing — endpoint REST simples, sem
  // subscription. Buscamos pra todas as machines ao montar.
  useEffect(() => {
    let mounted = true;
    const results = {};
    Promise.all(
      things.map(({ id }) =>
        getRendimentoHidraulico(id)
          .then(({ data }) => {
            results[id] = typeof data.value === 'number' ? data.value : null;
          })
          .catch(() => {
            results[id] = null;
          })
      )
    ).then(() => {
      if (mounted) setRendimentos({ ...results });
    });
    return () => {
      mounted = false;
    };
  }, [things]);

  // Calcula metricas derivadas por maquina. UCHA = Reversa - Ativa (positivo
  // em geracao normal); EBA = Ativa - Reversa OU EnergiaConsumidaTotal direto.
  const machines = useMemo(() => {
    return things.map((thing, idx) => {
      const data = thingsData[idx] || {};
      const ativa = Number(data.EnergiaAtiva) || 0;
      const reversa = Number(data.EnergiaAtivaReversa) || 0;
      const energy =
        type === 'eba'
          ? data.EnergiaConsumidaTotal !== undefined
            ? Number(data.EnergiaConsumidaTotal) || 0
            : ativa - reversa
          : reversa - ativa;
      const potencia = Number(data.PotenciaAtivaMedia) || 0;
      const vazao = Number(data.Vazao) || 0;
      const rendimento = rendimentos[thing.id];
      // Status grosso: erro se houver ErroAtivo ou alguma word de bloqueio;
      // atencao se houver advertencia; normal caso contrario.
      const hasBlock = Object.keys(data).some(
        (k) => k.startsWith('BloqueioWord') && data[k] && data[k] !== 0
      );
      const hasAlarm =
        data.ErroAtivo ||
        data.FalhaAtiva ||
        Object.keys(data).some(
          (k) => k.startsWith('AlarmeWord') && data[k] && data[k] !== 0
        );
      const hasWarn = Object.keys(data).some(
        (k) => k.startsWith('AdvertenciaWord') && data[k] && data[k] !== 0
      );
      let status = 'normal';
      if (hasBlock || hasAlarm) status = 'erro';
      else if (hasWarn) status = 'atencao';
      else if (potencia === 0 && vazao === 0) status = 'parada';
      return {
        thingProps: thing,
        name: thing.thing,
        id: thing.id,
        energy,
        potencia,
        vazao,
        rendimento,
        status,
        hasData: thingsData[idx] !== undefined,
      };
    });
  }, [things, thingsData, rendimentos, type]);

  // Ordenacao para a lista — mantemos a ordem original como fallback
  const sortedMachines = useMemo(() => {
    const list = [...machines];
    const sortFn = {
      energy: (a, b) => b.energy - a.energy,
      rendimento: (a, b) => (b.rendimento || 0) - (a.rendimento || 0),
      potencia: (a, b) => Math.abs(b.potencia) - Math.abs(a.potencia),
      vazao: (a, b) => b.vazao - a.vazao,
    }[sortBy];
    return sortFn ? list.sort(sortFn) : list;
  }, [machines, sortBy]);

  // Para o heatstrip: precisa do max em modulo para normalizar a altura.
  const maxEnergy = useMemo(
    () => Math.max(1, ...machines.map((m) => Math.abs(m.energy))),
    [machines]
  );

  const energyLabel = type === 'ucha' ? 'gerada' : 'consumida';

  const handleMachineClick = (m) => {
    if (onMachineClick) onMachineClick(m.thingProps);
  };

  if (!things || things.length === 0) {
    return (
      <s.Container>
        <s.EmptyState>Sem máquinas nesta aplicação.</s.EmptyState>
      </s.Container>
    );
  }

  return (
    <s.Container>
      {/* Heatstrip enxuto (barra de 14px) — so o ranking visual de relance.
          Detalhes ficam nos mini-cards abaixo. O titulo da secao ja vem
          do SectionTag externo (aplicacao.js wrapper). */}
      <s.HeatstripWrapper>
        <s.HeatstripLabel>Ranking por energia {energyLabel}</s.HeatstripLabel>
        <s.Heatstrip>
          {sortedMachines.map((m) => {
            const ratio = Math.abs(m.energy) / maxEnergy;
            return (
              <s.HeatstripCell
                key={`heat-${m.id}`}
                type="button"
                onClick={() => handleMachineClick(m)}
                $ratio={ratio}
                $status={m.status}
                $selected={selectedMachineId === m.id}
                title={`${m.name} · ${formatEnergyAutoUnit(m.energy, 'kWh')}`}
              >
                <s.HeatstripBar $ratio={ratio} $status={m.status} />
              </s.HeatstripCell>
            );
          })}
        </s.Heatstrip>
      </s.HeatstripWrapper>

      <s.SortBar>
        <s.SortLabel>Ordenar:</s.SortLabel>
        {[
          { key: 'energy', label: 'Energia' },
          { key: 'potencia', label: 'Potência' },
          { key: 'vazao', label: 'Vazão' },
          { key: 'rendimento', label: 'Rendimento' },
        ].map(({ key, label }) => (
          <s.SortChip
            key={key}
            type="button"
            aria-pressed={sortBy === key ? 'true' : 'false'}
            onClick={() => setSortBy(key)}
          >
            {label}
          </s.SortChip>
        ))}
      </s.SortBar>

      {/* Grid de mini-cards (2-4 colunas conforme largura). Cada card
          mostra todas as metricas da maquina em formato denso mas legivel.
          Substitui a lista vertical que so cabia 1 linha visivel. */}
      <s.MachineGrid>
        {sortedMachines.map((m) => (
          <s.MachineCard
            key={`card-${m.id}`}
            type="button"
            $selected={selectedMachineId === m.id}
            $status={m.status}
            onClick={() => handleMachineClick(m)}
          >
            <s.MachineCardHeader>
              <s.StatusDot $status={m.status} />
              <s.MachineName>{m.name}</s.MachineName>
            </s.MachineCardHeader>
            <s.MachineCardBody>
              <s.MachineCardMetric>
                <s.MachineCardMetricLabel>Energia</s.MachineCardMetricLabel>
                <s.MachineCardMetricValue negative={m.energy < 0}>
                  {formatEnergyAutoUnit(m.energy, 'kWh')}
                </s.MachineCardMetricValue>
              </s.MachineCardMetric>
              <s.MachineCardMetric>
                <s.MachineCardMetricLabel>Potência</s.MachineCardMetricLabel>
                <s.MachineCardMetricValue negative={m.potencia < 0}>
                  {formatPowerAutoUnit(m.potencia, 'kW')}
                </s.MachineCardMetricValue>
              </s.MachineCardMetric>
              <s.MachineCardMetric>
                <s.MachineCardMetricLabel>Vazão</s.MachineCardMetricLabel>
                <s.MachineCardMetricValue>
                  {m.vazao ? `${formatValue(m.vazao)} l/s` : '—'}
                </s.MachineCardMetricValue>
              </s.MachineCardMetric>
              <s.MachineCardMetric>
                <s.MachineCardMetricLabel>Rendimento</s.MachineCardMetricLabel>
                <s.MachineCardMetricValue
                  color={
                    m.rendimento === null || m.rendimento === undefined
                      ? undefined
                      : m.rendimento >= 80
                      ? 'green'
                      : m.rendimento >= 60
                      ? 'orange'
                      : 'red'
                  }
                >
                  {m.rendimento === null || m.rendimento === undefined
                    ? '—'
                    : `${formatValue(m.rendimento)}%`}
                </s.MachineCardMetricValue>
              </s.MachineCardMetric>
            </s.MachineCardBody>
          </s.MachineCard>
        ))}
      </s.MachineGrid>
    </s.Container>
  );
};

export default MachineAnalytics;
